import React, { useCallback, useEffect } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { useAirlineConditions } from "../../hooks/queries";
import { useCreateAirline } from "../../hooks/mutation";

import HeaderContainer from "../CrewDashboard/HeaderContainer";
import TextInput from "../common/TextInput";

const AirlineAddContainer = () => {
  const { data: conditions, isLoading: loadingConditions } =
    useAirlineConditions();

  return <AirlineAdd conditions={conditions} isLoading={loadingConditions} />;
};

const AirlineAdd = ({ conditions, isLoading }) => {
  const navigate = useNavigate(); // Use us

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: conditions ? conditions[0]?.CompanyID : 1,
      Code: "",
      Description: "",
      AirlineUser: "",
      AirlinePass: "",
      HotelUser: "",
      HotelPass: "",
      CrewUser: "",
      CrewPass: "",
      PortalPassReset: dayjs().format("YYYY-MM-DD"),
      Active: false,
    },
  });
  useEffect(() => {
    if (conditions) {
      setValue("CompanyID", conditions?.companyList.CompanyID);
    }
  }, [conditions, setValue]);

  const onSuccess = useCallback(() => {
    reset();
    toast.success("Success");
  }, [reset]);
  const onError = useCallback(() => {
    reset();
    toast.error("Failed at adding new airline");
  }, [reset]);
  const { mutate: createAirline, isPending: isCreating } = useCreateAirline(
    onSuccess,
    onError
  );

  const handleSave = useCallback(
    (_data) => {
      const data = {
        ..._data,
        PortalPassReset: _data.PortalPassReset
          ? dayjs(_data.PortalPassReset).format("YYYY-MM-DD HH:mm:ss")
          : undefined,
      };

      createAirline(data);
    },
    [createAirline]
  );
  const handleBack = useCallback(() => {
    sessionStorage.getItem("UserRole") === "1"
      ? navigate("/super-admin")
      : navigate("/manager");
  }, [navigate]);

  return isLoading ? null : (
    <div className="p-6 w-full">
      <HeaderContainer />
      {/* Top Section */}
      <div className="items-center mt-5 personal-info-content">
        <div
          onClick={handleBack}
          className="flex justify-between cursor-pointer items-center w-20 back-button"
        >
          <FaArrowLeft />
          <span>Back</span>
        </div>
        <p className="pt-4 font-semibold text-lg">Airline Add</p>
        <span>
          Add a driver, assign company flights, and update details here
        </span>
        <div className="w-100 mt-4 rounded-lg gap-1">
          <div className="flex justify-between mb-2">
            <div>
              <p>Company</p>
            </div>
            <Controller
              control={control}
              name="CompanyID"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between w-35">
                  <select
                    className="custom-input w-100 rounded-lg"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  >
                    {conditions?.companyList?.map((_company) => (
                      <option
                        value={_company.CompanyID}
                        key={`company-item-${_company.CompanyID}`}
                      >
                        {_company.Description}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            />
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Code</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Code"
                rules={{
                  required: "Code is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Code?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Description</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Description"
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Airline User</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="AirlineUser"
                rules={{
                  required: "Username required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.AirlineUser?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Airline Password</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="AirlinePass"
                rules={{
                  required: "Password required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    type="password"
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.AirlinePass?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Hotel User</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="HotelUser"
                rules={{
                  required: "Username required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.HotelUser?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>HotelPass</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="HotelPass"
                rules={{
                  required: "Password required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    type="password"
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.HotelPass?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Crew User</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="CrewUser"
                rules={{
                  required: "Username required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.CrewUser?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Crew Password</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="CrewPass"
                rules={{
                  required: "Password required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    type="password"
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.CrewPass?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Portal Pass Reset</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="PortalPassReset"
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Active</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Active"
                render={({ field: { value, onChange } }) => (
                  <ToggleSwitch
                    value={value}
                    onChange={(_value) => onChange(!value)}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mt-4 mb-4 save-button">
            <button
              onClick={handleSubmit(handleSave)}
              disabled={isCreating}
              className="justify-center p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              Add
            </button>
            <div>
              <div
                onClick={handleBack}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Clear</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AirlineAddContainer;

const ToggleSwitch = ({ value, onChange }) => {
  const onChangeHandler = useCallback(
    (e) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  return (
    <label className="switch">
      <input
        checked={value ? true : false}
        onChange={onChangeHandler}
        type="checkbox"
      />
      <span className="slider round"></span>
    </label>
  );
};
